import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

const RegisterPage = () => {
  const [role, setRole] = useState('writer');
  const [registrationStatus, setRegistrationStatus] = useState({});
  const navigate = useNavigate();
  const { login } = useAuth();

  // Check registration availability for proofreader and translator
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const [proofreaderRes, translatorRes] = await Promise.all([
          axios.get(`${API}/auth/registration-status/proofreader`).catch(() => ({ data: { available: true } })),
          axios.get(`${API}/auth/registration-status/translator`).catch(() => ({ data: { available: true } }))
        ]);
        setRegistrationStatus({
          proofreader: proofreaderRes.data,
          translator: translatorRes.data
        });
      } catch (error) {
        console.error('Failed to check registration status:', error);
      }
    };
    checkRegistrationStatus();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Link 
            to="/" 
            className="inline-block mb-4 text-orange-600 hover:text-orange-700 transition-colors"
          >
            ← Terug na Tuisblad
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Registreer by DIE BIBLIOTEEK
          </h1>
          <p className="text-gray-600">Kies jou rol om te begin</p>
        </div>

        <Tabs value={role} onValueChange={setRole} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="writer" data-testid="writer-tab">Skrywer</TabsTrigger>
            <TabsTrigger value="proofreader" data-testid="proofreader-tab">
              Proefleser
              {registrationStatus.proofreader && !registrationStatus.proofreader.available && (
                <span className="ml-1 text-red-500 text-xs">●</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="translator" data-testid="translator-tab">
              Vertaler
              {registrationStatus.translator && !registrationStatus.translator.available && (
                <span className="ml-1 text-red-500 text-xs">●</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="writer">
            <WriterRegistrationForm onSuccess={(token, user) => {
              login(token, user);
              toast.success('Registrasie suksesvol!');
              navigate('/writer');
            }} />
          </TabsContent>

          <TabsContent value="proofreader">
            {registrationStatus.proofreader && !registrationStatus.proofreader.available ? (
              <RegistrationClosedNotice 
                role="Proefleser" 
                message={registrationStatus.proofreader.message} 
              />
            ) : (
              <ProofreaderRegistrationForm onSuccess={() => {
                toast.success('Toets geslaag! Wag vir admin goedkeuring.');
                navigate('/login');
              }} />
            )}
          </TabsContent>

          <TabsContent value="translator">
            {registrationStatus.translator && !registrationStatus.translator.available ? (
              <RegistrationClosedNotice 
                role="Vertaler" 
                message={registrationStatus.translator.message} 
              />
            ) : (
              <TranslatorRegistrationForm onSuccess={() => {
                toast.success('Toets geslaag! Wag vir admin goedkeuring.');
                navigate('/login');
              }} />
            )}
          </TabsContent>
        </Tabs>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            Het jy reeds 'n rekening?{' '}
            <Link to="/login" className="text-orange-600 hover:text-orange-700 font-semibold">
              Meld aan
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Component to show when registration is closed
const RegistrationClosedNotice = ({ role, message }) => (
  <Card className="border-red-200 bg-red-50">
    <CardContent className="py-12 text-center">
      <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
      <h3 className="text-xl font-bold text-red-700 mb-2">
        {role} Registrasie Gesluit
      </h3>
      <p className="text-red-600 mb-4">
        {message || 'Daar is tans geen beskikbare posisies oop nie.'}
      </p>
      <p className="text-gray-600 text-sm">
        Probeer later weer of kontak die administrateur vir meer inligting.
      </p>
    </CardContent>
  </Card>
);

const WriterRegistrationForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    voornaam: '',
    van: '',
    email: '',
    password: '',
    geboortedatum: '',
    bank_naam: '',
    rekeningnommer: '',
    rekeningtipe: 'Cheque',
    takkode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Step 1: Personal info -> go to step 2 (T&C)
    if (step === 1) {
      handleNext();
      return;
    }
    
    // Step 2: T&C -> check acceptance and go to step 3 (Banking)
    if (step === 2) {
      if (!acceptedTerms) {
        toast.error('Jy moet die Terme en Voorwaardes aanvaar');
        return;
      }
      handleNext();
      return;
    }

    // Step 3: Banking -> Submit registration
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        ...formData,
        role: 'writer',
      });
      // Show success message but don't log in
      toast.success(response.data.message || 'Aansoek ingedien! Wag vir admin goedkeuring.');
      // Move to step 4 to show pending message
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrasie het misluk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-2 border-orange-200">
      <CardHeader>
        <CardTitle>Skrywer Registrasie</CardTitle>
        <CardDescription>
          {step === 4 
            ? 'Registrasie Voltooi' 
            : `Stap ${step} van 3: ${step === 1 ? 'Persoonlike Inligting' : step === 2 ? 'Terme en Voorwaardes' : 'Bankrekeninge'}`
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="writer-voornaam">Voornaam *</Label>
                  <Input
                    id="writer-voornaam"
                    data-testid="writer-voornaam-input"
                    required
                    value={formData.voornaam}
                    onChange={(e) => setFormData({ ...formData, voornaam: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="writer-van">Van *</Label>
                  <Input
                    id="writer-van"
                    data-testid="writer-van-input"
                    required
                    value={formData.van}
                    onChange={(e) => setFormData({ ...formData, van: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="writer-email">E-pos *</Label>
                <Input
                  id="writer-email"
                  data-testid="writer-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="writer-password">Wagwoord *</Label>
                <div className="relative">
                  <Input
                    id="writer-password"
                    data-testid="writer-password-input"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Minimum 8 karakters</p>
              </div>

              <div>
                <Label htmlFor="writer-geboortedatum">Geboortedatum * (Moet 15+ wees)</Label>
                <Input
                  id="writer-geboortedatum"
                  data-testid="writer-birthdate-input"
                  type="date"
                  required
                  value={formData.geboortedatum}
                  onChange={(e) => setFormData({ ...formData, geboortedatum: e.target.value })}
                />
              </div>
            </>
          )}

          {/* Step 2: Terms and Conditions */}
          {step === 2 && (
            <>
              <div className="mb-4 p-4 bg-orange-50 rounded-lg max-h-64 overflow-y-auto">
                <h3 className="font-semibold text-lg mb-2">Terme en Voorwaardes vir Skrywers</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>Deur te registreer by DIE BIBLIOTEEK, stem jy in tot die volgende:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Alle boeke wat jy indien is jou oorspronklike werk of jy het die regte om dit te publiseer</li>
                    <li>Boeke mag nie geplagieer wees of die regte van ander skend nie</li>
                    <li>Jy sal regstellings maak soos deur proeflees aanbeveel</li>
                    <li>Jy gee DIE BIBLIOTEEK toestemming om jou goedgekeurde boeke te publiseer</li>
                    <li>Jy behou volle outeurskap en kopiereg van jou boeke</li>
                    <li>Royalteit betalings sal verwerk word volgens platform terme</li>
                  </ul>
                  <p className="mt-3">
                    Vir volledige terme en voorwaardes, besoek ons{' '}
                    <a href="/terms-and-conditions" target="_blank" className="text-orange-600 hover:underline">
                      Terme en Voorwaardes bladsy
                    </a>.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="writer-accept-terms"
                  data-testid="writer-accept-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1"
                />
                <Label htmlFor="writer-accept-terms" className="cursor-pointer">
                  Ek aanvaar die Terme en Voorwaardes *
                </Label>
              </div>
            </>
          )}

          {/* Step 3: Banking Details */}
          {step === 3 && (
            <>
              <div className="mb-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  Ons benodig jou bankrekeninge om toekomstige royalteit betalings te verwerk.
                </p>
              </div>

              <div>
                <Label htmlFor="writer-bank-naam">Banknaam *</Label>
                <Input
                  id="writer-bank-naam"
                  data-testid="writer-bank-naam-input"
                  required
                  placeholder="bv. ABSA, FNB, Standard Bank"
                  value={formData.bank_naam}
                  onChange={(e) => setFormData({ ...formData, bank_naam: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="writer-rekeningnommer">Rekeningnommer *</Label>
                <Input
                  id="writer-rekeningnommer"
                  data-testid="writer-rekeningnommer-input"
                  required
                  placeholder="bv. 1234567890"
                  value={formData.rekeningnommer}
                  onChange={(e) => setFormData({ ...formData, rekeningnommer: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="writer-rekeningtipe">Rekeningtipe *</Label>
                <select
                  id="writer-rekeningtipe"
                  data-testid="writer-rekeningtipe-input"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                  value={formData.rekeningtipe}
                  onChange={(e) => setFormData({ ...formData, rekeningtipe: e.target.value })}
                >
                  <option value="Cheque">Cheque</option>
                  <option value="Savings">Savings</option>
                  <option value="Transmission">Transmission</option>
                </select>
              </div>

              <div>
                <Label htmlFor="writer-takkode">Takkode *</Label>
                <Input
                  id="writer-takkode"
                  data-testid="writer-takkode-input"
                  required
                  placeholder="bv. 632005"
                  value={formData.takkode}
                  onChange={(e) => setFormData({ ...formData, takkode: e.target.value })}
                />
              </div>
            </>
          )}

          {/* Step 4: Success Message */}
          {step === 4 && (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-green-600 mb-3">Aansoek Ingedien!</h3>
                <p className="text-gray-700 mb-4">
                  Dankie vir jou registrasie. Jou aansoek is suksesvol ingedien en wag nou vir admin goedkeuring.
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-orange-700">
                        <strong>Belangrik:</strong> Jy kan nog nie aanmeld nie. Kom oor 'n paar dae terug om te kyk of jou aansoek goedgekeur is. 
                        Sodra goedgekeur, sal jy kan aanmeld met jou e-pos en wagwoord.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Admin sal jou aansoek hersien en goedkeur. Ons verwerk gewoonlik aansoeke binne 2-3 werksdae.
                </p>
              </div>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                Terug na Tuisblad
              </Button>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1"
                >
                  Terug
                </Button>
              )}
              <Button
                data-testid="writer-submit-btn"
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                disabled={loading || (step === 2 && !acceptedTerms)}
              >
                {loading ? 'Besig...' : step < 3 ? 'Volgende' : 'Dien Aansoek In'}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

const ProofreaderRegistrationForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    voornaam: '',
    van: '',
    email: '',
    password: '',
    geboortedatum: '',
    bank_naam: '',
    rekeningnommer: '',
    rekeningtipe: 'Cheque',
    takkode: '',
  });
  const [testAnswers, setTestAnswers] = useState(Array(10).fill(''));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [testResult, setTestResult] = useState(null); // { passed: boolean, score: number }
  const [emailBlocked, setEmailBlocked] = useState(false);

  const correctAnswers = ['d', 'b', 'b', 'c', 'b', 'b', 'a', 'a', 'a', 'd'];  // Correct answers for all 10 questions

  const questions = [
    {
      question: "Watter sin het 'n spellingfout?",
      options: [
        { value: 'a', label: 'Die hond loop in die tuin' },
        { value: 'b', label: 'Hy gaan môre kom kuier' },
        { value: 'c', label: "Sy het 'n mooi kleed aan" },
        { value: 'd', label: 'Die kinders speel buiteuit' },  // Correct: should be "buite"
      ],
    },
    {
      question: "Waar hoort die komma: 'As jy kom moet jy jou sleutels saambring'?",
      options: [
        { value: 'a', label: 'Geen komma nodig' },
        { value: 'b', label: "Na 'kom'" },
        { value: 'c', label: "Na 'jy'" },
        { value: 'd', label: "Voor 'moet'" },
      ],
    },
    {
      question: 'Watter woord is verkeerd gespel?',
      options: [
        { value: 'a', label: 'Moontlik' },
        { value: 'b', label: 'Belangrek' },
        { value: 'c', label: 'Gelukkig' },
        { value: 'd', label: 'Vriendelik' },
      ],
    },
    {
      question: "Wat is verkeerd met: 'Die boek wat lê op die tafel'?",
      options: [
        { value: 'a', label: "'wat' moet 'wie' wees" },
        { value: 'b', label: "'lê' moet 'le' wees" },
        { value: 'c', label: 'Niks verkeerd nie' },
        { value: 'd', label: "'tafel' moet 'tafels' wees" },
      ],
    },
    {
      question: "Watter leesteken ontbreek: 'Hy vra Kom jy saam'",
      options: [
        { value: 'a', label: "Komma na 'vra'" },
        { value: 'b', label: "Dubbelpunt na 'vra'" },
        { value: 'c', label: 'Vraagteken aan die einde' },
        { value: 'd', label: 'Aanhalingstekens' },
      ],
    },
    {
      question: 'Wat is die korrekte vorm?',
      options: [
        { value: 'a', label: 'tussen jy en ek' },
        { value: 'b', label: 'tussen jou en my' },
        { value: 'c', label: 'tussen jy en my' },
        { value: 'd', label: 'tussen jou en ek' },
      ],
    },
    {
      question: 'Watter sin is grammatikaal korrek?',
      options: [
        { value: 'a', label: 'Hy het gister gekom' },
        { value: 'b', label: 'Hy sal gister kom' },
        { value: 'c', label: 'Hy gaan gister kom' },
        { value: 'd', label: 'Hy kom gister' },
      ],
    },
    {
      question: "Hoe skryf jy die meervoud van 'kind'?",
      options: [
        { value: 'a', label: 'kinders' },
        { value: 'b', label: 'kindes' },
        { value: 'c', label: 'kinder' },
        { value: 'd', label: 'kindere' },
      ],
    },
    {
      question: 'Wat is die korrekte skryfwyse?',
      options: [
        { value: 'a', label: 'alhoewel' },
        { value: 'b', label: 'al hoewel' },
        { value: 'c', label: 'alhoe wel' },
        { value: 'd', label: 'al-hoewel' },
      ],
    },
    {
      question: 'Watter sin het korrekte woordorde?',
      options: [
        { value: 'a', label: 'Gister het hy die boek gekoop' },
        { value: 'b', label: 'Hy het gister die boek gekoop' },
        { value: 'c', label: 'Die boek het hy gister gekoop' },
        { value: 'd', label: 'Al drie is korrek' },
      ],
    },
  ];

  const checkEmailBlock = async (email) => {
    try {
      const emailCheckData = new FormData();
      emailCheckData.append('email', email);
      const response = await axios.post(`${API}/check-email-block`, emailCheckData);
      if (response.data.blocked) {
        setEmailBlocked(true);
        toast.error(`Hierdie e-pos is geblokkeer. Probeer weer oor ${response.data.days_remaining} dae.`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking email block:', error);
      return false;
    }
  };

  const calculateTestScore = () => {
    let correct = 0;
    testAnswers.forEach((answer, index) => {
      if (answer === correctAnswers[index]) {
        correct++;
      }
    });
    return { score: correct, percentage: (correct / 10) * 100 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Step 1: Move to terms
    if (step === 1) {
      // Check if email is blocked
      const blocked = await checkEmailBlock(formData.email);
      if (blocked) {
        return;
      }
      setStep(2);
      return;
    }
    
    // Step 2: Terms accepted, move to test
    if (step === 2) {
      setStep(3);
      return;
    }
    
    // Step 3: Check test results
    if (step === 3) {
      const result = calculateTestScore();
      const passed = result.percentage >= 70;
      
      setTestResult({
        passed,
        score: result.score,
        percentage: result.percentage
      });
      
      if (passed) {
        // Pass - move to bank details
        toast.success(`Baie geluk! Jy het ${result.score}/10 (${result.percentage}%) behaal!`);
        setTimeout(() => {
          setStep(4);
        }, 2000);
      } else {
        // Fail - block email for 30 days
        try {
          const failedTestData = new FormData();
          failedTestData.append('email', formData.email);
          await axios.post(`${API}/record-failed-test`, failedTestData);
          toast.error(`Jou aansoek was onsuksesvol. Jy het ${result.score}/10 (${result.percentage}%) behaal. Jy kan weer probeer in 30 dae.`);
        } catch (error) {
          console.error('Error recording failed test:', error);
        }
      }
      return;
    }
    
    // Step 4: Final submission
    if (step === 4) {
      setLoading(true);

      try {
        const response = await axios.post(`${API}/auth/register`, {
          ...formData,
          role: 'proofreader',
          test_answers: testAnswers,
        });
        toast.success(response.data.message || 'Toets geslaag! Wag vir admin goedkeuring.');
        // Move to step 5 to show pending message
        setStep(5);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Registrasie het misluk');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Card className="border-2 border-orange-200">
      <CardHeader>
        <CardTitle>Proefleser Registrasie{step < 5 ? ` - Stap ${step} van 4` : ''}</CardTitle>
        <CardDescription>
          {step === 1 && 'Vul jou persoonlike inligting in'}
          {step === 2 && 'Lees en aanvaar die terme en voorwaardes'}
          {step === 3 && 'Voltooi die kwalifikasie toets (70% om te slaag)'}
          {step === 4 && 'Verskaf jou bankbesonderhede'}
          {step === 5 && 'Registrasie Voltooi'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {step === 1 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pr-voornaam">Voornaam *</Label>
                  <Input
                    id="pr-voornaam"
                    data-testid="proofreader-voornaam-input"
                    required
                    value={formData.voornaam}
                    onChange={(e) => setFormData({ ...formData, voornaam: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="pr-van">Van *</Label>
                  <Input
                    id="pr-van"
                    data-testid="proofreader-van-input"
                    required
                    value={formData.van}
                    onChange={(e) => setFormData({ ...formData, van: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pr-email">E-pos *</Label>
                <Input
                  id="pr-email"
                  data-testid="proofreader-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="pr-password">Wagwoord *</Label>
                <div className="relative">
                  <Input
                    id="pr-password"
                    data-testid="proofreader-password-input"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="pr-geboortedatum">Geboortedatum * (Moet 18+ wees)</Label>
                <Input
                  id="pr-geboortedatum"
                  data-testid="proofreader-birthdate-input"
                  type="date"
                  required
                  value={formData.geboortedatum}
                  onChange={(e) => setFormData({ ...formData, geboortedatum: e.target.value })}
                />
              </div>
            </>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 max-h-96 overflow-y-auto">
                <h3 className="text-lg font-bold mb-4">Terme en Voorwaardes vir Proeflees Dienste</h3>
                
                <div className="space-y-4 text-sm text-gray-700">
                  <section>
                    <h4 className="font-semibold text-base mb-2">1. Dienste en Verantwoordelikhede</h4>
                    <p>As 'n proefleser stem jy in om:</p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Boeke noukeurig te proef vir spelling, grammatika, en leestekens</li>
                      <li>'n Gedetailleerde foutlys te skep vir elke boek</li>
                      <li>Werk binne die ooreengekome tydlyn te voltooi</li>
                      <li>Professionele standaarde van proefleeswerk te handhaaf</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-base mb-2">2. Betaling</h4>
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Betaling van R2.00 per fout wat jy vind</li>
                      <li>Geen basisbetaling - verdienste gebaseer op foute gevind</li>
                      <li>Betalings word verwerk via EFT na goedkeuring deur admin</li>
                      <li>Betaling word slegs gemaak vir goedgekeurde werk</li>
                      <li>Jy is verantwoordelik vir jou eie belasting en ander finansiële verpligtinge</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-base mb-2">3. Vertroulikheid</h4>
                    <p>Jy stem in om alle boeke en inhoud vertroulik te hou en nie te deel, kopieer, of publiseer sonder toestemming nie.</p>
                  </section>

                  <section>
                    <h4 className="font-semibold text-base mb-2">4. Kwaliteit en Goedkeuring</h4>
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Alle werk word hersien deur admin</li>
                      <li>Werk van onvoldoende kwaliteit kan afgekeur word</li>
                      <li>Herhaalde lae kwaliteit werk kan lei tot verwydering van die platform</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-base mb-2">5. Onafhanklike Kontrakteur</h4>
                    <p>Jy werk as 'n onafhanklike kontrakteur, nie 'n werknemer nie. Jy is verantwoordelik vir jou eie belasting, UIF, en ander wetlike verpligtinge.</p>
                  </section>

                  <section>
                    <h4 className="font-semibold text-base mb-2">6. Beëindiging</h4>
                    <p>DIE BIBLIOTEEK behou die reg voor om jou toegang tot die platform te beëindig indien daar 'n skending van hierdie terme is.</p>
                  </section>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="accept-terms" className="text-sm text-gray-700 cursor-pointer">
                  Ek het die terme en voorwaardes gelees en verstaan, en ek aanvaar dit volledig. Ek verstaan dat ek as 'n onafhanklike kontrakteur werk en verantwoordelik is vir my eie belasting en wetlike verpligtinge.
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
              {questions.map((q, index) => (
                <div key={`question-${index}`} className="p-4 bg-orange-50 rounded-lg">
                  <Label className="text-base font-semibold mb-3 block">
                    {index + 1}. {q.question}
                  </Label>
                  <RadioGroup
                    value={testAnswers[index]}
                    onValueChange={(value) => {
                      const newAnswers = [...testAnswers];
                      newAnswers[index] = value;
                      setTestAnswers(newAnswers);
                    }}
                  >
                    {q.options.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2 py-2">
                        <RadioGroupItem value={option.value} id={`q${index}-${option.value}`} />
                        <Label htmlFor={`q${index}-${option.value}`} className="cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
            </div>
          )}

          {step === 4 && (
            <>
              <div>
                <Label htmlFor="bank_naam">Bank Naam *</Label>
                <Input
                  id="bank_naam"
                  data-testid="proofreader-bank-input"
                  required
                  value={formData.bank_naam}
                  onChange={(e) => setFormData({ ...formData, bank_naam: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="rekeningnommer">Rekeningnommer *</Label>
                <Input
                  id="rekeningnommer"
                  data-testid="proofreader-account-input"
                  required
                  value={formData.rekeningnommer}
                  onChange={(e) => setFormData({ ...formData, rekeningnommer: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="rekeningtipe">Rekeningtipe *</Label>
                <RadioGroup
                  value={formData.rekeningtipe}
                  onValueChange={(value) => setFormData({ ...formData, rekeningtipe: value })}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Cheque" id="cheque" />
                    <Label htmlFor="cheque">Cheque</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Savings" id="savings" />
                    <Label htmlFor="savings">Savings</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="takkode">Takkode *</Label>
                <Input
                  id="takkode"
                  data-testid="proofreader-branch-input"
                  required
                  value={formData.takkode}
                  onChange={(e) => setFormData({ ...formData, takkode: e.target.value })}
                />
              </div>
            </>
          )}

          {/* Step 5: Success Message */}
          {step === 5 && (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-green-600 mb-3">Aansoek Ingedien!</h3>
                <p className="text-gray-700 mb-4">
                  Baie geluk met die toets! Jou aansoek is suksesvol ingedien en wag nou vir admin goedkeuring.
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-orange-700">
                        <strong>Belangrik:</strong> Jy kan nog nie aanmeld nie. Kom oor 'n paar dae terug om te kyk of jou aansoek goedgekeur is. 
                        Sodra goedgekeur, sal jy kan aanmeld met jou e-pos en wagwoord.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Admin sal jou aansoek hersien en goedkeur. Ons verwerk gewoonlik aansoeke binne 2-3 werksdae.
                </p>
              </div>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                Terug na Tuisblad
              </Button>
            </div>
          )}

          {step < 5 && (
            <div className="flex gap-4">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(step - 1)}
                >
                  Terug
                </Button>
              )}
              <Button
                data-testid="proofreader-submit-btn"
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                disabled={loading || (step === 2 && !acceptedTerms) || (step === 3 && testAnswers.some(a => !a))}
              >
                {loading ? 'Besig...' : step === 4 ? 'Dien Aansoek In' : 'Volgende'}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

// Translator Registration Form
const TranslatorRegistrationForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    voornaam: '',
    van: '',
    email: '',
    password: '',
    geboortedatum: '',
    bank_naam: '',
    rekeningnommer: '',
    rekeningtipe: 'Cheque',
    takkode: '',
  });
  const [testAnswers, setTestAnswers] = useState(Array(10).fill(''));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [emailBlocked, setEmailBlocked] = useState(false);

  const correctAnswers = ['b', 'c', 'a', 'd', 'b', 'c', 'a', 'd', 'b', 'c'];

  const questions = [
    {
      question: "Wat is die korrekte Afrikaanse vertaling vir 'The children are playing outside'?",
      options: [
        { value: 'a', label: 'Die kinder speel buitekant' },
        { value: 'b', label: 'Die kinders speel buite' },
        { value: 'c', label: 'Die kinderen speel buiten' },
        { value: 'd', label: 'Die kind speel buite' },
      ],
    },
    {
      question: "Hoe vertaal jy 'I have been waiting for you' na Afrikaans?",
      options: [
        { value: 'a', label: 'Ek het gewag vir jou' },
        { value: 'b', label: 'Ek was wag vir jou' },
        { value: 'c', label: 'Ek wag al vir jou' },
        { value: 'd', label: 'Ek is wag vir jou' },
      ],
    },
    {
      question: "Wat is die beste vertaling vir 'She would have come if she had known'?",
      options: [
        { value: 'a', label: 'Sy sou gekom het as sy geweet het' },
        { value: 'b', label: 'Sy sal kom as sy weet' },
        { value: 'c', label: 'Sy het gekom as sy geweet' },
        { value: 'd', label: 'Sy wou kom as sy weet' },
      ],
    },
    {
      question: "Watter vertaling is korrek vir 'The book on the table belongs to me'?",
      options: [
        { value: 'a', label: 'Die boek op die tafel is myne' },
        { value: 'b', label: 'Die boek aan die tafel behoort my' },
        { value: 'c', label: 'Die boek by die tafel is my' },
        { value: 'd', label: 'Die boek op die tafel behoort aan my' },
      ],
    },
    {
      question: "Hoe vertaal jy 'as soon as possible'?",
      options: [
        { value: 'a', label: 'so soos moontlik' },
        { value: 'b', label: 'so gou as moontlik' },
        { value: 'c', label: 'so vinnig as kan' },
        { value: 'd', label: 'so spoedig moontlik' },
      ],
    },
    {
      question: "Wat is die korrekte vertaling vir 'in spite of the rain'?",
      options: [
        { value: 'a', label: 'in spyt van die reën' },
        { value: 'b', label: 'teen die reën' },
        { value: 'c', label: 'ten spyte van die reën' },
        { value: 'd', label: 'ondanks die reën' },
      ],
    },
    {
      question: "Hoe vertaal jy 'They are looking forward to the holiday'?",
      options: [
        { value: 'a', label: 'Hulle sien uit na die vakansie' },
        { value: 'b', label: 'Hulle kyk vorentoe na die vakansie' },
        { value: 'c', label: 'Hulle verwag die vakansie' },
        { value: 'd', label: 'Hulle bly vir die vakansie' },
      ],
    },
    {
      question: "Wat is die korrekte vertaling vir 'He used to live here'?",
      options: [
        { value: 'a', label: 'Hy het gebruik om hier te woon' },
        { value: 'b', label: 'Hy is gewoont om hier te woon' },
        { value: 'c', label: 'Hy woon gewoonlik hier' },
        { value: 'd', label: 'Hy het hier gewoon' },
      ],
    },
    {
      question: "Hoe vertaal jy 'neither...nor'?",
      options: [
        { value: 'a', label: 'nie...nie' },
        { value: 'b', label: 'nóg...nóg' },
        { value: 'c', label: 'of...of' },
        { value: 'd', label: 'en...en' },
      ],
    },
    {
      question: "Wat is die beste vertaling vir 'It goes without saying'?",
      options: [
        { value: 'a', label: 'Dit gaan sonder sê' },
        { value: 'b', label: 'Dit is duidelik' },
        { value: 'c', label: 'Dit spreek vanself' },
        { value: 'd', label: 'Dit sê sonder gaan' },
      ],
    },
  ];

  const checkEmailBlock = async (email) => {
    try {
      const emailCheckData = new FormData();
      emailCheckData.append('email', email);
      const response = await axios.post(`${API}/check-email-block`, emailCheckData);
      if (response.data.blocked) {
        setEmailBlocked(true);
        toast.error(`Hierdie e-pos is geblokkeer. Probeer weer oor ${response.data.days_remaining} dae.`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking email block:', error);
      return false;
    }
  };

  const calculateTestScore = () => {
    let correct = 0;
    testAnswers.forEach((answer, index) => {
      if (answer === correctAnswers[index]) {
        correct++;
      }
    });
    return { score: correct, percentage: (correct / 10) * 100 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      const blocked = await checkEmailBlock(formData.email);
      if (blocked) return;
      setStep(2);
      return;
    }
    
    if (step === 2) {
      setStep(3);
      return;
    }
    
    if (step === 3) {
      const result = calculateTestScore();
      const passed = result.percentage >= 70;
      
      setTestResult({
        passed,
        score: result.score,
        percentage: result.percentage
      });
      
      if (passed) {
        toast.success(`Baie geluk! Jy het ${result.score}/10 (${result.percentage}%) behaal!`);
        setTimeout(() => setStep(4), 2000);
      } else {
        try {
          const failedTestData = new FormData();
          failedTestData.append('email', formData.email);
          await axios.post(`${API}/record-failed-test`, failedTestData);
          toast.error(`Jou aansoek was onsuksesvol. Jy het ${result.score}/10 (${result.percentage}%) behaal. Jy kan weer probeer in 30 dae.`);
        } catch (error) {
          console.error('Error recording failed test:', error);
        }
      }
      return;
    }
    
    if (step === 4) {
      setLoading(true);

      try {
        const response = await axios.post(`${API}/auth/register`, {
          ...formData,
          role: 'translator',
          test_answers: testAnswers,
        });
        toast.success(response.data.message || 'Toets geslaag! Wag vir admin goedkeuring.');
        setStep(5);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Registrasie het misluk');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Card className="shadow-lg border-orange-100">
      <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-t-lg">
        <CardTitle className="text-2xl">Vertaler Registrasie</CardTitle>
        <CardDescription className="text-green-50">
          Word 'n professionele vertaler en verdien geld
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        {/* Progress indicator */}
        <div className="flex justify-between mb-8">
          {['Persoonlike Info', 'Terme', 'Toets', 'Bank', 'Klaar'].map((label, i) => (
            <div key={label} className={`flex flex-col items-center ${i + 1 <= step ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                i + 1 < step ? 'bg-green-600 text-white' :
                i + 1 === step ? 'bg-green-100 border-2 border-green-600' : 'bg-gray-200'
              }`}>
                {i + 1 < step ? '✓' : i + 1}
              </div>
              <span className="text-xs">{label}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="voornaam">Voornaam *</Label>
                  <Input id="voornaam" value={formData.voornaam} onChange={(e) => setFormData({...formData, voornaam: e.target.value})} required />
                </div>
                <div>
                  <Label htmlFor="van">Van *</Label>
                  <Input id="van" value={formData.van} onChange={(e) => setFormData({...formData, van: e.target.value})} required />
                </div>
              </div>
              <div>
                <Label htmlFor="email">E-pos *</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
              </div>
              <div>
                <Label htmlFor="password">Wagwoord *</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="geboortedatum">Geboortedatum *</Label>
                <Input id="geboortedatum" type="date" value={formData.geboortedatum} onChange={(e) => setFormData({...formData, geboortedatum: e.target.value})} required />
              </div>
            </div>
          )}

          {/* Step 2: Terms */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                <h3 className="font-semibold mb-2">Vertaler Terme en Voorwaardes</h3>
                <ul className="text-sm space-y-2 text-gray-700">
                  <li>• Jy moet 18 jaar of ouer wees</li>
                  <li>• Jy moet die vertalingstoets slaag (70% of hoër)</li>
                  <li>• Jy sal boeke aanvra en wag vir admin goedkeuring</li>
                  <li>• Kort boeke (&lt;100 bladsye): R150 basis</li>
                  <li>• Lang boeke (≥100 bladsye): R300 basis</li>
                  <li>• R2.00 word afgetrek vir elke fout gevind deur proefleser</li>
                  <li>• 84 of meer foute sal lei tot verwerping van die vertaling (geen betaling)</li>
                  <li>• 4 verwerpings sal lei tot skorsing van jou rekening</li>
                  <li>• Jy moet akkurate en professionele vertalings lewer</li>
                </ul>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="rounded" />
                <span className="text-sm">Ek aanvaar die terme en voorwaardes</span>
              </label>
            </div>
          )}

          {/* Step 3: Test */}
          {step === 3 && !testResult && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg">Vertalingstoets</h3>
              <p className="text-sm text-gray-600">Beantwoord al 10 vrae. Jy benodig 70% om te slaag.</p>
              {questions.map((q, qIndex) => (
                <div key={`tq-${qIndex}`} className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium mb-3">{qIndex + 1}. {q.question}</p>
                  <RadioGroup value={testAnswers[qIndex]} onValueChange={(value) => {
                    const newAnswers = [...testAnswers];
                    newAnswers[qIndex] = value;
                    setTestAnswers(newAnswers);
                  }}>
                    {q.options.map((opt) => (
                      <div key={opt.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt.value} id={`q${qIndex}-${opt.value}`} />
                        <Label htmlFor={`q${qIndex}-${opt.value}`}>{opt.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
            </div>
          )}

          {/* Test Result */}
          {step === 3 && testResult && (
            <div className={`p-6 rounded-lg text-center ${testResult.passed ? 'bg-green-50' : 'bg-red-50'}`}>
              <h3 className={`text-2xl font-bold mb-2 ${testResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                {testResult.passed ? 'Geslaag!' : 'Druip'}
              </h3>
              <p className="text-lg">Jou telling: {testResult.score}/10 ({testResult.percentage}%)</p>
              {!testResult.passed && <p className="mt-2 text-sm text-gray-600">Jy kan weer probeer in 30 dae.</p>}
            </div>
          )}

          {/* Step 4: Bank Details */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold">Bank Besonderhede</h3>
              <div>
                <Label htmlFor="bank_naam">Bank Naam *</Label>
                <Input id="bank_naam" value={formData.bank_naam} onChange={(e) => setFormData({...formData, bank_naam: e.target.value})} required placeholder="bv. ABSA, FNB, Capitec" />
              </div>
              <div>
                <Label htmlFor="rekeningnommer">Rekeningnommer *</Label>
                <Input id="rekeningnommer" value={formData.rekeningnommer} onChange={(e) => setFormData({...formData, rekeningnommer: e.target.value})} required />
              </div>
              <div>
                <Label htmlFor="rekeningtipe">Rekening Tipe *</Label>
                <select id="rekeningtipe" value={formData.rekeningtipe} onChange={(e) => setFormData({...formData, rekeningtipe: e.target.value})} className="w-full p-2 border rounded">
                  <option value="Cheque">Tjek/Lopende</option>
                  <option value="Savings">Spaar</option>
                  <option value="Transmission">Transmissie</option>
                </select>
              </div>
              <div>
                <Label htmlFor="takkode">Takkode *</Label>
                <Input id="takkode" value={formData.takkode} onChange={(e) => setFormData({...formData, takkode: e.target.value})} required placeholder="bv. 632005" />
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✓</span>
              </div>
              <h3 className="text-xl font-bold text-green-600 mb-2">Aansoek Ingedien!</h3>
              <p className="text-gray-600 mb-4">Jou vertaler aansoek is ingedien en wag vir admin goedkeuring.</p>
              <Button type="button" onClick={() => navigate('/')} className="bg-gradient-to-r from-green-500 to-green-600">
                Terug na Tuisblad
              </Button>
            </div>
          )}

          {step < 5 && (
            <div className="flex gap-4">
              {step > 1 && (
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(step - 1)}>
                  Terug
                </Button>
              )}
              <Button
                data-testid="translator-submit-btn"
                type="submit"
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                disabled={loading || (step === 2 && !acceptedTerms) || (step === 3 && testAnswers.some(a => !a))}
              >
                {loading ? 'Besig...' : step === 4 ? 'Dien Aansoek In' : 'Volgende'}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default RegisterPage;